import { CurrencyPipe, DecimalPipe } from '@angular/common';
import { Inject, Injectable, Optional } from '@angular/core';
import { BasketService, MODAL_ROUTES } from '@woolworthsnz/shop';
import {
	ANALYTICS_PROVIDER,
	AnalyticsClass,
	ApiService,
	AppSettingsService,
	CustomWindow,
	DatalayerService,
	HttpArgs,
	ListType,
	ModalEvent,
	ModalOverlayService,
	ModalRoutingService,
	ProductDatalayerEvent,
	PromotionService,
	ShopperService,
	ShopperState,
	StatefulService,
	WINDOW,
	ProductType,
	PromotionalCarouselResponseModel,
	SearchTarget,
	FeatureService,
} from '@woolworthsnz/styleguide';
import {
	AddItemsToSavedList,
	AddOrUpdateProductPreferencesRequest,
	AddOrUpdateTrolleyRequest,
	AddOrUpdateTrolleyResponse,
	BaseResponse,
	ContextResponse,
	FulfilmentStoreStockLevelResponse,
	PriceResponse,
	ProductDetailsResponse,
	ProductResponse,
	ProductStoresStockLevelResponse,
	ProductTag,
	QuantityResponse,
	ShopperPreferencesResponse,
	ShoppingListItem,
	SizeResponse,
	TrolleyItemResponse,
} from '@woolworthsnz/trader-api';
import { StampSettings } from '../components/stamp/stamp.settings';
import * as httpStatus from 'http-status-codes';
import { EMPTY, Observable, throwError } from 'rxjs';
import { catchError, map, take, tap } from 'rxjs/operators';
import { TealiumProduct } from '@woolworthsnz/analytics';
import { extractQLCodeFromCtaName, isProductAvailable, isProductDetailsReponse, isProductReponse } from '../helpers';
import { CitrusAdService } from './citrus-ad.service';
import { ProductState } from './product.state';
import { FulfilmentStoreService } from '@woolworthsnz/fulfilment';
import StockLevelEnum = FulfilmentStoreStockLevelResponse.StockLevelEnum;
import { BoostsFacade } from '@woolworthsnz/everyday-rewards';
import { ProductSource } from '../models/tealium-events';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { getItemVariant } from '../helpers/product.service.helper';

@Injectable()
export class ProductService extends StatefulService<ProductState> {
	shopper: ShopperState;
	isGA4AddToCartItemVariantEnabled$: boolean;
	trolleyMessageTitle = `Just a minute...`;
	promotion: PromotionalCarouselResponseModel;
	shopperState$ = this.shopperService.state$.pipe(takeUntilDestroyed());
	cartologyNewPastOrder = 'Cartology - New Past Order';
	cartologyNewFavourites = 'Cartology - New Favourites';
	searchCigCarousel = 'Search CIG Carousel';
	browseCigCarousel = 'Browse CIG Carousel';

	constructor(
		public apiService: ApiService,
		private currencyPipe: CurrencyPipe,
		private decimalPipe: DecimalPipe,
		private datalayerService: DatalayerService,
		private modalOverlayService: ModalOverlayService,
		private shopperService: ShopperService,
		private appSettingsService: AppSettingsService,
		private citrusAdService: CitrusAdService,
		private basketService: BasketService,
		private fulfilmentStoreService: FulfilmentStoreService,
		private modalRoutingService: ModalRoutingService,
		private promotionService: PromotionService,
		private boostsFacade: BoostsFacade,
		private featureService: FeatureService,
		@Optional() @Inject(ANALYTICS_PROVIDER) private tealiumService: AnalyticsClass,
		@Inject(WINDOW) private window: CustomWindow
	) {
		super(new ProductState());

		this.shopperState$.subscribe((s) => (this.shopper = s));
		this.isGA4AddToCartItemVariantEnabled$ = this.featureService.isFeatureEnabled(
			ContextResponse.EnabledFeaturesEnum.EnableGA4AddToCartItemVariant
		);
	}

	get cupPrice(): string | false {
		return this.size && this.size.cupMeasure && this.size.cupPrice
			? `${this.getPriceAsCurrency(this.size.cupPrice)} / ${this.size.cupMeasure}`
			: false;
	}

	get noMemberCupPrice(): string {
		return this.size && this.size.cupMeasure && this.size.cupListPrice
			? `${this.getPriceAsCurrency(this.size.cupListPrice)} / ${this.size.cupMeasure}`
			: '';
	}

	get multiCupPrice(): string {
		const isMultiBuyExist = this.productTag?.multiBuy?.multiCupValue && this.size?.cupMeasure;
		return !!isMultiBuyExist
			? `${this.getPriceAsCurrency(this.productTag?.multiBuy?.multiCupValue ?? '')} / ${this.size.cupMeasure}`
			: '';
	}

	get shouldShowMultiBuyCupPrice(): boolean {
		const multiBuyCupTagsList = [
			ProductTag.TagTypeEnum.IsGreatPriceMultiBuy,
			ProductTag.TagTypeEnum.IsMultiBuy,
			ProductTag.TagTypeEnum.IsOnlineOnly,
		];
		return multiBuyCupTagsList.includes(this.productTag?.tagType as ProductTag.TagTypeEnum);
	}

	get hasShopperNotes(): boolean {
		return this.state.hasShopperNotes;
	}

	get product(): ProductResponse | ProductDetailsResponse | null {
		return this.state.product;
	}

	get quantityInTrolley(): number {
		return this.state.quantityInTrolley;
	}

	get salePrice(): number | undefined {
		return this.product?.price?.salePrice;
	}

	get price(): PriceResponse | undefined {
		return this.product?.price;
	}

	get savePrice(): number | undefined {
		return this.product?.price?.savePrice;
	}

	get discount(): string | undefined {
		return this.product?.price?.discount;
	}

	get total(): string | undefined {
		return this.product?.price?.total;
	}

	get productIsAvailable(): boolean {
		return isProductAvailable({ price: this.price, quantity: this.quantityResponse });
	}

	get productIsInStock(): boolean {
		return this.product?.availabilityStatus?.toString().toLowerCase() !== 'out of stock' && this.productIsAvailable;
	}

	get productAvailabilityStatus(): string | undefined {
		return this.product?.availabilityStatus;
	}

	get quantityResponse(): QuantityResponse | undefined {
		return this.product?.quantity;
	}

	get selectedPurchasingUnit(): ProductResponse.UnitEnum {
		return this.state?.selectedPurchasingUnit || this.product?.selectedPurchasingUnit;
	}

	get shopperNote(): string {
		return this.state?.shopperNote;
	}

	get size(): SizeResponse | undefined {
		return this.product?.size;
	}

	get productTag(): ProductTag | undefined {
		const product = this.product as ProductResponse;
		return product.productTag;
	}

	get sku(): string | undefined {
		return this.product?.sku;
	}

	get adId(): string | undefined {
		if (this.product && 'adId' in this.product) {
			return this.product?.adId;
		}
		return undefined;
	}

	get boughtBefore(): boolean {
		if (this.product && 'boughtBefore' in this.product) {
			return this.product.boughtBefore === true;
		}
		return false;
	}

	get supportsBothEachAndKgPricing(): boolean | undefined {
		return (this.product as ProductResponse)?.supportsBothEachAndKgPricing;
	}

	get unit(): 'Each' | 'Kg' | undefined {
		return this.product?.unit;
	}

	// Pricing unit differs from unit as it is the logic relating to whether the shopper has decided to
	// purchase Kg items as loose (each) items
	get pricingUnit(): ProductResponse.UnitEnum | undefined {
		if (!this.supportsBothEachAndKgPricing) {
			return this.unit;
		}
		return this.selectedPurchasingUnit ?? ProductResponse.UnitEnum.Each;
	}

	get isEachKgItemAdded(): boolean | undefined {
		return this.supportsBothEachAndKgPricing && this.quantityInTrolley > 0;
	}

	canOpenChangePickupStoreModal(mhlsPickupAddressId?: number): boolean {
		if (!mhlsPickupAddressId) {
			return false;
		}
		const pickupAddressId = this.fulfilmentStoreService.state.pickupAddressId;
		const fulfilmentStoreStockLevels =
			(this.state.product as ProductDetailsResponse)?.productStoresStockLevel?.fulfilmentStoreStockLevels || [];
		const stockLevel = fulfilmentStoreStockLevels[0]?.stockLevel;
		const hasDifferentPickupAddressId = pickupAddressId !== mhlsPickupAddressId;
		const inStock = stockLevel !== StockLevelEnum.NoStock && stockLevel !== StockLevelEnum.Unknown;
		return inStock && hasDifferentPickupAddressId;
	}

	openChangePickupStoreModal(mhlsPickupAddressId?: number, mhlsPickupStoreName?: string): void {
		this.modalRoutingService.openWithOptions(
			MODAL_ROUTES.PRODUCT_DETAILS_MODAL,
			{
				state: { mhlsPickupAddressId, mhlsPickupStoreName, productService: this },
				skipLocationChange: true,
			},
			MODAL_ROUTES.CHANGE_STOREFRONT_PICKUP_STORE_PATH
		);
	}

	openChangedPriceModal(
		quantity: any,
		priceChangeDetails: any,
		source: ProductSource,
		updateProductDetails: boolean,
		showActionButtons: boolean
	): void {
		this.modalRoutingService.openWithOptions(
			MODAL_ROUTES.MODAL_OUTLET,
			{
				skipLocationChange: true,
				state: {
					productService: this,
					quantity,
					priceChangeDetails,
					source,
					updateProductDetails,
					showActionButtons,
				},
			},
			MODAL_ROUTES.CHANGED_PRICE_MODAL_PATH
		);
	}

	addOrUpdateTrolley(
		quantity: number,
		source: ProductSource = {} as ProductSource,
		updateProductDetails = false,
		validatePriceChange = false,
		productType: ProductType | undefined = undefined,
		position: string | undefined = undefined,
		cppItemCount?: number,
		pageSource?: string,
		target?: string
	): void {
		if (!this.shopper.isShopper) {
			this.modalOverlayService.open({
				eventType: ModalEvent.addToTrolley,
			});

			return;
		}

		if (!this.product) {
			return;
		}

		const minimumAllowedProductQuantity = this.getMinimumAllowedProductQuantity();
		const quantityToSend = quantity < minimumAllowedProductQuantity ? 0 : quantity;
		const body = this.getAddOrUpdateTrolleyRequestBody(quantityToSend);

		const args: HttpArgs = {};
		// Returns the complete new product model
		args.params = { returnProductDetails: updateProductDetails, validatePriceChange: validatePriceChange };

		this.apiService
			.post(`${this.appSettingsService.getEndpoint('trolley')}/my/items`, body, args)
			.pipe(
				take(1),
				catchError((error) => {
					if (!error?.error?.priceChangeDetails) {
						this.handleError(error);
						this.afterTrolleySubmitRequest();
						return throwError(error);
					}
					this.afterTrolleySubmitRequest();
					this.openChangedPriceModal(
						quantityToSend,
						error.error.priceChangeDetails,
						source,
						updateProductDetails,
						true
					);
					return EMPTY;
				}),
				map((r) => r as AddOrUpdateTrolleyResponse)
			)
			.subscribe((response) =>
				this.handleAddOrUpdateTrolleyResponse(
					response,
					this.quantityInTrolley,
					source,
					updateProductDetails,
					productType,
					position,
					pageSource,
					target,
					cppItemCount
				)
			);
	}

	allowSubstitution = (): Observable<any> =>
		this.apiService.put(this.getSubstitutionEndpoint(), {}).pipe(catchError(this.handleSubstitutionError));

	disallowSubstitution = (): Observable<any> =>
		this.apiService.delete(this.getSubstitutionEndpoint()).pipe(catchError(this.handleSubstitutionError));

	getSubstitutionEndpoint = (): string =>
		`${this.appSettingsService.getEndpoint('trolley')}/my/items/${this.sku}/substitutions`;

	handleSubstitutionError = (error: any): Observable<never> => {
		this.setState({ error });
		return throwError(error);
	};

	getAddOrUpdateTrolleyRequestBody(quantity: number): AddOrUpdateTrolleyRequest {
		// IE11 and Safari < 10 cannot handle URLSearchParams so we pass an empty string
		// The usage is low so the searches would not massively impact the boosting
		const searchString =
			typeof URLSearchParams !== 'undefined'
				? new URLSearchParams((<any>this.window)['location']?.search)?.get('search') || undefined
				: undefined;
		const requestBody: AddOrUpdateTrolleyRequest = {
			sku: this.product?.sku || '',
			quantity,
			searchString,
			pricingUnit: this.pricingUnit,
			adId: undefined,
			brandSuggestionId: undefined,
		};

		if (this.product && 'adId' in this.product) {
			requestBody.adId = this.product?.adId;
		}

		requestBody.brandSuggestionId = this.promotionService.trackConversion(quantity, this.window.location.pathname);

		const productDetailsResponse = this.product as ProductResponse;
		if (productDetailsResponse && productDetailsResponse.onlineSampleRealProductMapId) {
			requestBody.onlineSampleRealProductMapId = productDetailsResponse.onlineSampleRealProductMapId;
		}

		return requestBody;
	}

	handleAddOrUpdateTrolleyResponse(
		response: AddOrUpdateTrolleyResponse,
		currentQuantity: any,
		source: ProductSource,
		updateProductDetails: boolean,
		productType: ProductType | undefined = undefined,
		position: string | undefined = undefined,
		pageSource: string | undefined = undefined,
		target: string | undefined = undefined,
		cppItemCount?: number
	): void {
		let newQuantity = 0;
		this.basketService.expressTotalQuantityToRemove = response.totalExpressItemsToRemoveFromBasket || 0;
		if (response.autoboostedOfferIds) {
			this.boostsFacade.markOffersAsBoosted(response.autoboostedOfferIds);
		}

		if (response.itemAdded) {
			newQuantity = response.itemAdded.quantity || 0;
		}

		// If the user has sent a quantity which was rejected by the back end,
		// but still successfully added to the basket
		if (response.messages?.length) {
			this.modalOverlayService.open({
				eventType: ModalEvent.addToTrolleyError,
				errorText: response.messages[0],
				errorTitle: this.trolleyMessageTitle,
			});
		}

		const unit = this.getUnitForTracking(response.itemAdded);
		if (this.product) {
			this.trackProductEvent(
				this.product,
				currentQuantity,
				newQuantity,
				unit,
				source,
				pageSource,
				target,
				'productTag' in this.product ? this.product.productTag?.tagType : undefined,
				productType,
				position,
				cppItemCount
			);
		}
		if (this.product !== null) {
			this.trackCitrusAdClick(this.product, currentQuantity, newQuantity);
		}

		const shouldUpdateSelectedPurchasingUnit =
			this.product?.supportsBothEachAndKgPricing &&
			this.product?.selectedPurchasingUnit !== response.itemAdded?.unit;
		let newState: Partial<ProductState> = {
			// Don't allow the completed animation to trigger on 0 quantity
			completed: !!newQuantity,
			quantityInTrolley: newQuantity,
			submitted: false,
			...(shouldUpdateSelectedPurchasingUnit && { selectedPurchasingUnit: response.itemAdded?.unit }),
			product: {
				...this.product,
				...(shouldUpdateSelectedPurchasingUnit && { selectedPurchasingUnit: response.itemAdded?.unit }),
				quantity: {
					...this.product?.quantity,
					value: newQuantity,
				},
			},
		};

		if (updateProductDetails && response.itemAdded?.item) {
			// If we requested the complete product details model we should update state with the new product
			const productDetails = { ...response.itemAdded.item };
			if (this.product && 'adId' in this.product) {
				productDetails.adId = this.product.adId;
			}
			newState = {
				...newState,
				product: productDetails,
			};
			if (response.promotionAffectedItems) {
				newState.promotionAffectedItems = response.promotionAffectedItems.reduce((acc, product) => {
					if (product.sku) {
						acc.set(product.sku, product);
					}
					return acc;
				}, new Map<string, TrolleyItemResponse>());
			}
		}
		this.setState(newState);
	}

	addToList(savedListId: string, itemsToAdd: ShoppingListItem[]): Observable<any> {
		if (!savedListId) {
			return throwError('No saved list id supplied');
		}

		if (!this.product || !this.product.sku) {
			return throwError('Product not defined');
		}

		const addEndpoint = StampSettings.listEndpoint({
			listId: savedListId,
			sku: this.product.sku,
			listType: ListType.SavedList,
		});

		const body: AddItemsToSavedList = {
			itemsToAdd,
		};

		return this.apiService.post(addEndpoint, body).pipe(
			catchError((error) => {
				this.setState({
					error,
				});

				return throwError(error);
			})
		);
	}

	addOrUpdateShopperNotes(addOrUpdateShopperPreferencesRequest: AddOrUpdateProductPreferencesRequest): void {
		if (!this.shopper.isShopper) {
			this.modalOverlayService.open({
				eventType: ModalEvent.addShopperNote,
			});

			return;
		}

		const note = addOrUpdateShopperPreferencesRequest.shopperNotes;

		const hasDifferentShopperNote = note && note !== this.shopperNote;
		const hasDeletedShopperNote = this.shopperNote && !note;

		if (hasDifferentShopperNote || hasDeletedShopperNote) {
			this.shopperService
				.addOrUpdateShopperNotes(addOrUpdateShopperPreferencesRequest)
				.pipe(
					take(1),
					catchError((error) => {
						this.setState({
							error,
						});

						return throwError(error);
					})
				)
				.subscribe((r: BaseResponse) => {
					if (!!r.isSuccessful) {
						this.setState({
							hasShopperNotes: !!note,
							shopperNote: note || '',
							error: undefined,
						});
					}
				});

			return;
		}

		this.setState({
			shopperNote: '',
			hasShopperNotes: false,
		});
	}

	afterTrolleySubmitRequest = (): void => {
		this.setState({
			submitted: false,
		});
	};

	getProductDetail = (sku: string, fulfilmentStoreId?: string): Observable<ProductDetailsResponse> => {
		let storeQueryParam = '';
		if (fulfilmentStoreId) {
			storeQueryParam = `?store=${fulfilmentStoreId}`;
		}

		return this.apiService
			.get(`${this.appSettingsService.getEndpoint('productDetails')}/${sku}${storeQueryParam}`, {})
			.pipe(
				catchError((error) => {
					this.setState({
						error,
					});
					return throwError(error);
				}),
				tap(() => {
					this.setState({});
				})
			);
	};

	getProductStoresStockLevels(sku: string, storeIds: number[]): Observable<ProductStoresStockLevelResponse> {
		const storeIdsStr = storeIds.join(',');
		return this.apiService
			.get(
				`${this.appSettingsService.getEndpoint(
					'productStockLevels'
				)}/${sku}?stores=${storeIdsStr}&includeAddress=true`,
				{}
			)
			.pipe(
				catchError((error) => {
					this.setState({
						error,
					});

					return throwError(error);
				}),
				tap((response) => {
					this.setState({
						...this.state,
						fulfilmentStoreStockLevels: response?.fulfilmentStoreStockLevels,
					});
				})
			);
	}

	refetchProductDetail = (): void => {
		if (!this.product?.sku) {
			return;
		}
		const sku = this.product.sku;
		this.getProductDetail(sku)
			.pipe(take(1))
			.subscribe((response) => {
				if (this.product && 'adId' in this.product) {
					this.setState({
						product: { ...response, adId: this.product.adId },
					});
				} else {
					this.setState({
						product: { ...response },
					});
				}
			});
	};

	getSavedListsWithProduct = (sku: string): Observable<any> =>
		this.apiService.get(`${this.appSettingsService.getEndpoint('productDetailSavedList')}/${sku}`, {}).pipe(
			catchError((error) => {
				this.setState({
					error,
				});

				return throwError(error);
			})
		);

	getShopperNotes(sku: string): void {
		this.shopperService
			.getShopperNotes({ sku })
			.pipe(
				take(1),
				catchError((error) => {
					this.setState({
						error,
						shopperNote: this.shopperNote,
					});

					return throwError(error);
				})
			)
			.subscribe((response: ShopperPreferencesResponse) => {
				this.setState({
					shopperNote: response.shopperProductNotes || '',
				});
			});
	}

	getPriceAsCurrency(price: string | number): string {
		return this.currencyPipe.transform(price, 'NZD', 'symbol-narrow', '1.2-2') || '';
	}

	getAmountToDisplay(val: number): string | null {
		const displayInGrams = this.decimalPipe.transform(val * 1000, `1.0-0`);
		const displayInKG = this.decimalPipe.transform(val, `1.0-3`);

		return val < 1 ? displayInGrams : displayInKG;
	}

	handleError = (e: { status: any; message: string | undefined; error: any }): void => {
		switch (e.status) {
			case httpStatus.StatusCodes.BAD_REQUEST:
			case httpStatus.StatusCodes.GATEWAY_TIMEOUT:
			case httpStatus.StatusCodes.INTERNAL_SERVER_ERROR:
			case httpStatus.StatusCodes.NOT_FOUND:
				this.modalOverlayService.open({
					eventType: ModalEvent.genericError,
				});
				break;
			case httpStatus.StatusCodes.UNAUTHORIZED:
				this.modalOverlayService.open({
					eventType: ModalEvent.notAuthorised,
				});
				break;
			case httpStatus.StatusCodes.UNPROCESSABLE_ENTITY:
				this.modalOverlayService.open({
					eventType: ModalEvent.addToTrolleyError,
					errorText: e.error.Messages[0],
					errorTitle: this.trolleyMessageTitle,
				});
				break;
		}

		this.setState({
			error: e,
			completed: false,
		});
	};

	removeFromList(listId: string, listType: ListType = ListType.SavedList): Observable<any> {
		if (!this.shopper.isShopper) {
			this.modalOverlayService.open({
				eventType: ModalEvent.deleteFromList,
			});

			return throwError('Not authorised');
		}

		if (!listId && listType === ListType.SavedList) {
			return throwError('Invalid list id');
		}
		if (!this.product || !this.product.sku) {
			return throwError('No product defined');
		}

		const deleteEndpoint = StampSettings.listEndpoint({ listId, sku: this.product.sku, listType });

		return this.apiService.delete(deleteEndpoint).pipe(
			catchError((error) => {
				this.setState({
					error,
				});

				return throwError(error);
			})
		);
	}

	setPurchasingUnit(selectedPurchasingUnit: ProductResponse.UnitEnum): void {
		if (this.selectedPurchasingUnit === selectedPurchasingUnit) {
			return;
		}
		this.setState({
			selectedPurchasingUnit,
		});
	}

	trackProductEvent(
		product: ProductResponse | ProductDetailsResponse,
		oldQuantity = 0,
		quantity = 0,
		unit: TrolleyItemResponse.UnitEnum | undefined,
		source: ProductSource,
		pageSource: string | undefined,
		target: string | undefined,
		specialLabel?: string,
		productType?: ProductType | undefined,
		position?: string | undefined,
		cppItemCount?: number
	): void {
		if (quantity === oldQuantity) {
			return;
		}
		const itemListName = this.getItemListName(target);
		const itemVariant = this.isGA4AddToCartItemVariantEnabled$
			? getItemVariant(pageSource, productType)
			: getItemVariant(pageSource, undefined);

		const { breadcrumb, adId, brand, availabilityStatus, promotionId, promotionName } =
			this.getTrackProductEventData(product);
		const quantityDifference: number = Math.abs(oldQuantity - quantity);
		const dataToPush = <ProductDatalayerEvent>{
			id: product.sku ?? 0,
			name: product.name,
			price: product.price && product.price.salePrice,
			availabilityStatus,
			quantity: quantityDifference,
			saleUnit: unit,
			carouselName: source?.name,
			specialLabel,
			...(productType && { productType: productType }),
			...(position && { position: position }),
			...(typeof cppItemCount !== 'undefined' && { cppItemCount: cppItemCount }),
			...(!productType && adId && { cartologySponsoredProduct: 'Yes' }),
		};

		const productObject: TealiumProduct = {
			item_id: product.sku ?? '',
			item_name: product.name ?? '',
			coupon: '',
			discount: product.price?.savePrice,
			index: position,
			item_brand: brand,
			item_category: breadcrumb?.department?.name,
			item_category2: breadcrumb?.aisle?.name,
			item_category3: breadcrumb?.shelf?.name,
			item_list_id: source?.id,
			item_list_name: itemListName,
			item_variant: itemVariant,
			price: product.price?.salePrice,
			quantity: quantity,
			each_kg: unit,
			promotion_id: promotionId,
			promotion_name: promotionName,
		};

		if (quantity > oldQuantity) {
			this.datalayerService.addOrUpdateTrolley(dataToPush);
		} else {
			this.datalayerService.removeFromTrolley(dataToPush);
		}

		this.tealiumService.link({
			tealium_event: quantity > oldQuantity ? 'add_to_cart' : 'remove_from_cart',
			ecommerce: {
				currency: 'NZD',
				value: product.price?.salePrice,
				items: [productObject],
			},
		});
	}

	trackCitrusAdClick(product: ProductResponse | ProductDetailsResponse, currentQuantity = 0, newQuantity = 0): void {
		if (newQuantity === currentQuantity) {
			return;
		}

		if (newQuantity > currentQuantity && currentQuantity === 0) {
			// only report on 1st add to trolley
			this.citrusAdService.reportClick(product);
		}
	}

	/**
	 * Convert weightInKg to number of units if applicable.
	 * Only does a conversion if average weight per unit is specified.
	 * If not passed, passed kg value is returned.
	 *
	 * Note: When weightInKg is not divisible by averageWeightPerUnit,
	 * then rounds up to the nearest integer
	 * @param weightInKg
	 * @param averageWeightPerUnit
	 */
	getEachQuantityForGivenWeight(weightInKg: number, averageWeightPerUnit: number | undefined): number {
		if (!averageWeightPerUnit) {
			return weightInKg;
		}

		// Need to round first to avoid errors due to division precision
		// eg. 2.7/0.3 = 9.000000000000002
		return Math.ceil(Math.fround(weightInKg / averageWeightPerUnit));
	}

	addMinimumProductQuantity(
		source: ProductSource = {} as ProductSource,
		pageSource?: string,
		target?: string,
		updateProductDetails = false,
		productType?: ProductType,
		position?: string,
		cppItemCount?: number
	): void {
		this.addOrUpdateTrolley(
			this.getMinimumAllowedProductQuantity(),
			source,
			updateProductDetails,
			false,
			productType,
			position,
			cppItemCount,
			pageSource,
			target
		);
	}

	getMinimumAllowedProductQuantity(): number {
		const minimumQtyWithDefaultFallback =
			this.product?.quantity?.min || (this.pricingUnit === ProductResponse.UnitEnum.Each ? 1 : 0.1);
		const calculatedMinimumQty =
			this.pricingUnit === ProductResponse.UnitEnum.Each
				? this.getEachQuantityForGivenWeight(minimumQtyWithDefaultFallback, this.product?.averageWeightPerUnit)
				: minimumQtyWithDefaultFallback;
		return calculatedMinimumQty;
	}

	private getItemListName(target: string | undefined): string {
		return (
			(target &&
				{
					[SearchTarget.Search]: this.searchCigCarousel,
					[SearchTarget.Browse]: this.browseCigCarousel,
				}[target]) ||
			''
		);
	}

	private getTrackProductEventData(product: ProductResponse | ProductDetailsResponse): {
		breadcrumb: any;
		adId: any;
		brand: any;
		availabilityStatus: string;
		promotionId: string;
		promotionName: string;
	} {
		let breadcrumb;
		let adId;
		let brand;

		if (isProductDetailsReponse(product)) {
			breadcrumb = product?.breadcrumb || {};
		}

		if (isProductReponse(product)) {
			adId = product?.adId;
			brand = product?.brand;
		}

		let availabilityStatus = 'Unavailable';
		if (this.productIsInStock) {
			availabilityStatus = 'In stock';
		} else if (this.productIsAvailable) {
			availabilityStatus = 'Out of stock';
		}
		const promotionId = this.promotion
			? extractQLCodeFromCtaName(this.promotion.name) ?? this.promotion.id.toString()
			: '';
		const promotionName = this.promotion?.name ?? '';

		return { breadcrumb, adId, brand, availabilityStatus, promotionId, promotionName };
	}

	private getUnitForTracking(itemAdded: TrolleyItemResponse | undefined): TrolleyItemResponse.UnitEnum | undefined {
		const responseUnit = itemAdded?.unit;
		const selectedUnit = itemAdded?.selectedPurchasingUnit;
		if (!!selectedUnit && responseUnit !== selectedUnit) {
			return selectedUnit;
		} else {
			return responseUnit;
		}
	}
}
